/**
 * Kalenderweergave van het draaidag-overzicht: één rij per cameraman/regisseur,
 * dagen als kolommen, draaidagen als blokken (wiki-feedback 9 sep 2026:
 * "lanes per regisseur + cameraman, kleur op bezetting").
 *
 * Pure functies zonder React, zodat de naam-koppeling en het dagvenster los
 * testbaar zijn. Draaidagen bewaren crew als korte naam ("Isa"); de week-check
 * kent mensen bij hun volledige naam ("Isa Jansen") met rol. Die twee koppelen
 * we op de voornaam — en alléén als die voornaam bij één persoon hoort, anders
 * gokken we niet en komt de naam onder "Overige crew".
 */
import { addDays, formatDateISO, getMonday } from './weekUtils';
import { legInLanes } from './inplannen';

export const ROL_SECTIES = [
  { key: 'Camera', label: 'Camera' },
  { key: 'Regie', label: 'Regie' },
  { key: 'Overig', label: 'Overige crew' },
];

const WEEKDAG_KORT = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

/** ISO-weeknummer van een datum (YYYY-MM-DD). */
export function isoWeekNummer(iso) {
  const d = new Date(`${iso}T12:00:00`);
  const target = new Date(d.valueOf());
  target.setDate(target.getDate() + 3 - ((d.getDay() + 6) % 7));
  const jan4 = new Date(target.getFullYear(), 0, 4);
  return 1 + Math.round(((target - jan4) / 86400000 - 3 + ((jan4.getDay() + 6) % 7)) / 7);
}

/** Alle dagen in het venster, met weeksleutel (maandag) zoals de week-check die kent. */
export function dagenVenster(maandagISO, weken) {
  const maandag = getMonday(new Date(`${maandagISO}T12:00:00`));
  const dagen = [];
  for (let i = 0; i < weken * 7; i += 1) {
    const dag = addDays(maandag, i);
    const weekStart = addDays(maandag, Math.floor(i / 7) * 7);
    dagen.push({
      iso: formatDateISO(dag),
      dagNr: dag.getDate(),
      weekdag: WEEKDAG_KORT[i % 7],
      weekend: i % 7 >= 5,
      weekKey: formatDateISO(weekStart),
      weekNr: isoWeekNummer(formatDateISO(weekStart)),
      eersteVanWeek: i % 7 === 0,
    });
  }
  return dagen;
}

/** Weken in het venster: [{ weekKey, weekNr, kolom, breedte }]. */
export function wekenVanVenster(dagen) {
  const weken = [];
  dagen.forEach((d, i) => {
    if (d.eersteVanWeek) weken.push({ weekKey: d.weekKey, weekNr: d.weekNr, kolom: i, breedte: 0 });
    weken[weken.length - 1].breedte += 1;
  });
  return weken;
}

const normaliseer = (s) => String(s || '').trim().toLowerCase();
const voornaam = (s) => normaliseer(s).split(/\s+/)[0] || '';

/**
 * Personen (naam + rol) uit de week-check halen. De check levert per week vier
 * lijsten (over / op max / ruimte / druk); samen over alle weken zijn dat vrijwel
 * altijd alle actieve cameramensen en regisseurs.
 */
export function personenUitWeekChecks(weekChecks = {}) {
  const map = new Map();
  Object.values(weekChecks || {}).forEach((wk) => {
    ['over', 'op_max', 'ruimte', 'druk_met_agenda'].forEach((lijst) => {
      (wk?.[lijst] || []).forEach((p) => {
        if (p?.naam && !map.has(p.naam)) map.set(p.naam, { naam: p.naam, rol: p.rol || 'Overig', email: p.email || '' });
      });
    });
  });
  return [...map.values()];
}

/** Bezetting per persoon per week: `${weekKey}|${naam}` → { toestand, draaidagen, max }. */
export function bezettingUitWeekChecks(weekChecks = {}) {
  const uit = {};
  Object.entries(weekChecks || {}).forEach(([weekKey, wk]) => {
    const zet = (lijst, toestand) => (wk?.[lijst] || []).forEach((p) => {
      const sleutel = `${weekKey}|${p.naam}`;
      // 'over' wint van 'druk', 'druk' van 'op_max', 'op_max' van 'ruimte'.
      const rang = { over: 3, druk: 2, op_max: 1, ruimte: 0 };
      if (!uit[sleutel] || rang[toestand] > rang[uit[sleutel].toestand]) {
        uit[sleutel] = { toestand, draaidagen: p.draaidagen ?? 0, max: p.max ?? 0 };
      }
    });
    zet('ruimte', 'ruimte'); zet('op_max', 'op_max'); zet('druk_met_agenda', 'druk'); zet('over', 'over');
  });
  return uit;
}

/**
 * Korte crew-naam → persoon. Eén persoon met die voornaam = koppelen; nul of
 * meer dan één = null (dan komt de naam als eigen rij onder "Overige crew").
 */
export function koppelCrewNaam(crewNaam, personen) {
  const n = normaliseer(crewNaam);
  if (!n) return null;
  const exact = personen.find((p) => normaliseer(p.naam) === n);
  if (exact) return exact;
  const opVoornaam = personen.filter((p) => voornaam(p.naam) === voornaam(n));
  return opVoornaam.length === 1 ? opVoornaam[0] : null;
}

/** Positie van een draaidag in het venster, of null als hij er buiten valt. */
export function plaatsDraaidag(r, dagen) {
  if (!dagen.length || !r?.startDatum) return null;
  const eerste = dagen[0].iso;
  const laatste = dagen[dagen.length - 1].iso;
  const start = String(r.startDatum).slice(0, 10);
  let eind = String(r.eindDatum || '').slice(0, 10);
  if (!eind || eind < start) {
    const n = Math.max(1, Number(r.dagen) || 1);
    eind = formatDateISO(addDays(new Date(`${start}T12:00:00`), n - 1));
  }
  if (eind < eerste || start > laatste) return null;
  const van = start < eerste ? eerste : start;
  const tot = eind > laatste ? laatste : eind;
  const kolom = dagen.findIndex((d) => d.iso === van);
  const eindKolom = dagen.findIndex((d) => d.iso === tot);
  if (kolom === -1 || eindKolom === -1) return null;
  return { kolom, breedte: eindKolom - kolom + 1, afgekaptVoor: start < eerste, afgekaptNa: eind > laatste };
}

/**
 * De rijen van de kalender.
 *
 * @param rows        draaidagen (gefilterd) uit het overzicht
 * @param personen    [{ naam, rol }] — uit de week-check
 * @param dagen       dagenVenster(...)
 * @param opties      { alleenMetBlokken } — bij een actief filter alleen rijen met werk
 * @returns { secties: [{ key, label, rijen: [{ naam, rol, blokken, lanes }] }], zonderCrew: { blokken, lanes } }
 */
export function bouwKalenderRijen(rows, personen, dagen, opties = {}) {
  const perRij = new Map(); // sleutel → { naam, rol, items }
  const rijSleutel = (p) => `${p.rol}|${p.naam}`;
  personen.forEach((p) => perRij.set(rijSleutel(p), { naam: p.naam, rol: p.rol, email: p.email, items: [] }));
  const zonderCrewItems = [];

  rows.forEach((r) => {
    const plek = plaatsDraaidag(r, dagen);
    if (!plek) return;
    const item = { ...r, ...plek };
    const crew = Array.isArray(r.crew) ? r.crew : [];
    let geplaatst = false;
    crew.forEach((naam) => {
      const persoon = koppelCrewNaam(naam, personen);
      const sleutel = persoon ? rijSleutel(persoon) : `Overig|${String(naam).trim()}`;
      if (!perRij.has(sleutel)) perRij.set(sleutel, { naam: persoon?.naam || String(naam).trim(), rol: persoon?.rol || 'Overig', items: [] });
      const rij = perRij.get(sleutel);
      if (!rij.items.some((x) => x.id === r.id)) rij.items.push(item);
      if (persoon && persoon.rol !== 'Overig') geplaatst = true;
    });
    if (!geplaatst) zonderCrewItems.push(item);
  });

  const naarRij = (rij) => {
    const blokken = legInLanes(rij.items);
    return { ...rij, blokken, lanes: Math.max(1, ...blokken.map((b) => b.lane + 1)) };
  };
  const secties = ROL_SECTIES.map((s) => ({
    ...s,
    rijen: [...perRij.values()]
      .filter((rij) => rij.rol === s.key)
      .map(naarRij)
      .filter((rij) => !opties.alleenMetBlokken || rij.blokken.length > 0)
      .sort((a, b) => a.naam.localeCompare(b.naam, 'nl')),
  })).filter((s) => s.rijen.length > 0);
  const zonderCrew = naarRij({ naam: 'Zonder camera/regie', rol: null, items: zonderCrewItems });
  return { secties, zonderCrew };
}
